import React from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton, FormHelperText } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Industry } from '../types/types';

export interface IndustryTemplateValues {
  leadConversionRate: number;
  averageDealSize: number;
  salesCycleLengthMonths: number;
}

const INDUSTRY_TEMPLATES: Record<Exclude<Industry, 'Custom'>, IndustryTemplateValues> = {
  'SaaS': { leadConversionRate: 25, averageDealSize: 50000, salesCycleLengthMonths: 3 },
  'Manufacturing': { leadConversionRate: 15, averageDealSize: 200000, salesCycleLengthMonths: 6 },
  'Professional Services': { leadConversionRate: 35, averageDealSize: 25000, salesCycleLengthMonths: 2 },
  'E-commerce': { leadConversionRate: 20, averageDealSize: 75000, salesCycleLengthMonths: 4 },
};

interface IndustrySelectorProps {
  value: Industry;
  onChange: (industry: Industry) => void;
  onApplyTemplate: (template: IndustryTemplateValues) => void;
  error?: boolean;
  helperText?: string;
}

const IndustrySelector: React.FC<IndustrySelectorProps> = ({ value, onChange, onApplyTemplate, error, helperText }) => {
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selected = event.target.value as Industry;
    onChange(selected);
    if (selected !== 'Custom') {
      const template = INDUSTRY_TEMPLATES[selected as Exclude<Industry, 'Custom'>];
      onApplyTemplate(template);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FormControl fullWidth error={error} aria-label="Industry template selector">
        <InputLabel sx={{ 
          fontSize: '1.3rem',
          fontWeight: 600,
          color: '#33bbdf',
          '&.Mui-focused': {
            color: '#33bbdf'
          }
        }}>Industry Template</InputLabel>
        <Select 
          value={value} 
          label="Industry Template" 
          onChange={handleChange as any}
          sx={{
            '& .MuiSelect-select': {
              fontSize: '1.1rem'
            }
          }}
        >
          <MenuItem value={'SaaS'} sx={{ fontSize: '1.1rem' }}>SaaS</MenuItem>
          <MenuItem value={'Manufacturing'} sx={{ fontSize: '1.1rem' }}>Manufacturing</MenuItem>
          <MenuItem value={'Professional Services'} sx={{ fontSize: '1.1rem' }}>Professional Services</MenuItem>
          <MenuItem value={'E-commerce'} sx={{ fontSize: '1.1rem' }}>E-commerce</MenuItem>
          <MenuItem value={'Custom'} sx={{ fontSize: '1.1rem' }}>Custom</MenuItem>
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
      <Tooltip title="Based on industry benchmarks" aria-label="Based on industry benchmarks">
        <IconButton size="small" sx={{ ml: 1 }}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default IndustrySelector;


